import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import { type JobStore } from '@/features/jobs/JobStore'
import { type DraftJobStore } from './DraftJobStore'
import { NotFound } from 'fejl'
import { type PrefilledDraftInput } from './PrefilledDraftInput'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import {
  asapScheduler,
  defer,
  merge,
  type MonoTypeOperatorFunction,
  NEVER,
  Observable,
  of,
  Subject,
} from 'rxjs'
import {
  filter,
  find,
  map,
  observeOn,
  share,
  switchMap,
  tap,
} from 'rxjs/operators'
import { Id } from '@/infra/id'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type ModalViewModel } from '@/infra/ModalViewModel'
import { toStream } from '@/utils/mobx-util'
import { every, whileActive } from '@/utils/rx-util'
import { Job } from '@/features/jobs/Job'
import { dateFormatters, timeago } from '@/utils/date-util'
import { PublishedLegend } from '@/features/jobs/legends/PublishedLegend'
import { DeleteDraftJobDialogViewModel } from '@/features/jobs/drafting/components/DeleteDraftJobDialog/DeleteDraftJobDialogViewModel'
import { RenameJobDialogViewModel } from '@/features/jobs/RenameJobDialog/RenameJobDialogViewModel'
import { type IntakeQuestionsViewModelFactory } from './IntakeQuestionsViewModel'
import { type LegendSelectorViewModelFactory } from './LegendSelectorViewModel'
import { OutlineViewModel, type StepAccessor } from './OutlineViewModel'
import {
  type DraftChange,
  type DraftPersister,
  type DraftPersisterFactory,
  type LegendSelected,
} from './DraftPersister'
import { type PreviousProviderSelectionViewModelFactory } from './PreviousProviderSelectionViewModel'
import { type SubmissionViewModelFactory } from './SubmissionViewModel'
import { type TeamSelectorViewModelFactory } from './TeamSelectorViewModel'
import {
  type WorkspaceSelectorViewModel,
  type WorkspaceSelectorViewModelFactory,
} from './WorkspaceSelectorViewModel'
import { CopyJobDialogViewModel } from '@/features/jobs/CopyJobDialog/CopyJobDialogViewModel'
import { NameInputViewModel } from './NameInputViewModel'
import { type OwnerSelectorViewModelFactory } from './OwnerSelectorViewModel'
import { type RequestProviderSelectorViewModelFactory } from './RequestProviderSelectorViewModel'
import { type SupportingClientSelectorViewModelFactory } from './SupportingClientSelectorViewModel'

/**
 * View-model for drafting a job, from picking the workspace through to
 * submission.
 *
 * Makes heavy use of Rx streams due to their automatic cleanup trait.
 * For example, the Job Rename and Delete dialogs will close when unsubscribed
 * from since if we navigate back and forth we don't want to keep the dialogs open.
 */
export class DraftJobViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Emit a value when the Job is deleted.
   *
   * @private
   */
  private readonly _jobDeleted = new Subject<void>()

  /**
   * Whether the draft was submitted or deleted, after which it is no longer
   * edited.
   */
  @observable private accessor _isFinished = false

  /**
   * Persister. Maintains the Job instance.
   */
  readonly persister

  /**
   * Stream for the job being drafted once we have it.
   * Emits the current value on subscribe, or `Job.none` if there
   * is no draft job selected.
   */
  readonly job$

  /**
   * Outline view model. Manages all the steps.
   */
  readonly outline

  /**
   * Name.
   */
  readonly nameInput

  /**
   * Workspace selector.
   */
  readonly workspaceSelector: WorkspaceSelectorViewModel

  /**
   * Owner selector.
   */
  readonly ownerSelector

  /**
   * Team selector.
   */
  readonly teamSelector

  /**
   * Supporting client selector.
   */
  readonly supportingClientSelector

  /**
   * Request provider selector.
   */
  readonly requestProviderSelector

  /**
   * Legend selector.
   */
  readonly legendSelector

  /**
   * Intake questions.
   */
  readonly intakeQuestions

  /**
   * Provider selection.
   */
  readonly previousProviderSelection

  /**
   * Submission.
   */
  readonly submission

  /**
   * State slices.
   */
  readonly stateSlices: DraftStateSlice[]

  /**
   * Emit a value when the Draft was successfully submitted.
   */
  readonly jobSubmitted$

  /**
   * Emit a value when the Draft Job is created.
   */
  readonly jobCreated$

  /**
   * Emit a value when we detect an attempt to load a non-draft Job.
   */
  readonly notADraft$

  /**
   * Emit a value when the Draft Job is deleted.
   */
  readonly jobDeleted$

  /**
   * Job rename modal.
   */
  readonly renameModal

  /**
   * Dialog for deleting the draft.
   */
  readonly deleteDialogVm

  /**
   * Job copy modal.
   */
  readonly copyModal

  /**
   * Constructor.
   */
  constructor(
    private readonly _createWorkspaceSelector: WorkspaceSelectorViewModelFactory,
    private readonly _createOwnerSelector: OwnerSelectorViewModelFactory,
    private readonly _createTeamSelector: TeamSelectorViewModelFactory,
    private readonly _createSupportingClientSelector: SupportingClientSelectorViewModelFactory,
    private readonly _createRequestProviderSelector: RequestProviderSelectorViewModelFactory,
    private readonly _createLegendSelector: LegendSelectorViewModelFactory,
    private readonly _createIntakeQuestions: IntakeQuestionsViewModelFactory,
    private readonly _createPreviousProviderSelection: PreviousProviderSelectionViewModelFactory,
    private readonly _createSubmission: SubmissionViewModelFactory,
    private readonly _createDraftPersister: DraftPersisterFactory,
    private readonly _jobStore: JobStore,
    private readonly _draftJobStore: DraftJobStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _navigate: NavigateFn,
    requestRename$: Observable<unknown>,
    private readonly _canEditDraftJobsAsAdmin: (workspaceId: Id) => boolean,
    private readonly _disabled: () => boolean,
  ) {
    this.persister = this._createPersister()

    this.job$ = this.persister.job$

    this.outline = new OutlineViewModel(
      // Defers activating the last pending step until we have all the data to
      // make that decision.
      toStream(() => this.prepare.resolved, true).pipe(find(Boolean)),
      () => this.stepAccessors,
    )

    this.nameInput = new NameInputViewModel(
      this.outline.createStep,
      () => this.disabled,
      () => this.canEditDraftJobsAsAdmin,
    )

    this.workspaceSelector = this._createWorkspaceSelector(
      this.outline.createStep,
      () => this.jobWasCreated || this.disabled,
    )

    this.ownerSelector = this._createOwnerSelector(
      this.outline.createStep,
      this.workspaceSelector.selectedWorkspaceMember$,
      () => this.jobWasCreated || this.disabled,
      () => this.canEditDraftJobsAsAdmin,
    )

    this.teamSelector = this._createTeamSelector(
      this.outline.createStep,
      this.ownerSelector.selectedOwner$,
      () => this.jobWasCreated || this.disabled,
      () => this.canEditDraftJobsAsAdmin,
    )

    this.supportingClientSelector = this._createSupportingClientSelector(
      this.outline.createStep,
      this.workspaceSelector.selectedWorkspaceMember$,
      this.ownerSelector.selectedOwner$,
      () => this.disabled,
      () => this.jobWasCreated,
    )

    this.requestProviderSelector = this._createRequestProviderSelector(
      this.outline.createStep,
      this.workspaceSelector.selectedWorkspaceMember$,
      this.ownerSelector.selectedOwner$,
      () => this.disabled,
      () => this.canEditDraftJobsAsAdmin,
    )

    this.legendSelector = this._createLegendSelector(
      this.outline.createStep,
      this.workspaceSelector.selectedWorkspaceMember$,
      () => this.disabled,
    )

    this.intakeQuestions = this._createIntakeQuestions(
      this.outline.createStep,
      () => this.disabled,
      this.job$,
      this.legendSelector.changes$
        .pipe(
          switchMap(() =>
            merge(
              of(null),
              this.persister.persistedChanges$.pipe(
                filter((c): c is LegendSelected => c.type === 'LegendSelected'),
                map((x) => x.legend),
              ),
            ),
          ),
        )
        .pipe(share()),
    )

    this.previousProviderSelection = this._createPreviousProviderSelection(
      this.outline.createStep,
      this.workspaceSelector.selectedWorkspaceMember$,
      // Don't return the selected Legend until we have a Job instance.
      // This will prevent the step from showing up until then.
      this.job$.pipe(
        switchMap(() => this.legendSelector.selectedLegendVersion$),
      ),
      () => this.disabled,
      () => this.canEditDraftJobsAsAdmin,
    )

    this.submission = this._createSubmission(
      this.outline.createStep,
      this.persister.queueChange,
      this.workspaceSelector.selectedWorkspaceMember$,
      this.teamSelector.selectedTeam$,
      this.job$,
      this.legendSelector.selectedLegendVersion$,
      every([
        this.ownerSelector.isValid$,
        this.workspaceSelector.selectedWorkspaceMember$,
        this.teamSelector.selectedTeam$,
        this.legendSelector.selectedLegendVersion$,
        this.intakeQuestions.allQuestionsCompleted$,
        this.previousProviderSelection.isValid$,
      ]),
      // Since we start the loading indicator on persist start, we also want to react to persist errors
      // to keep things running.
      this.persister.persist$.pipe(
        filter((r) => r.persistedChanges.length > 0 || !!r.errorMessage),
      ),
      this.persister.persisting$,
      () => this._submitRequest(),
      () => this.stepAccessors.every((sc) => sc.step.isCompleted),
      () => this.disabled,
      () => this._copyDraft(this.job),
      () => this.canEditDraftJobsAsAdmin,
    )

    this.stateSlices = [
      this.workspaceSelector,
      this.nameInput,
      this.ownerSelector,
      this.teamSelector,
      this.supportingClientSelector,
      this.requestProviderSelector,
      this.legendSelector,
      this.intakeQuestions,
      this.previousProviderSelection,
      this.submission,
    ]

    this.jobSubmitted$ = this.submission.submitted$.pipe(
      switchMap(() => this.job$),
      this._whileActive(),
      share(),
    )

    this.jobCreated$ = this.persister.jobCreated$.pipe(
      this._whileActive(),
      share(),
    )

    this.notADraft$ = this.persister.notADraft$.pipe(
      this._whileActive(),
      share(),
    )

    this.jobDeleted$ = this._jobDeleted.pipe(this._whileActive(), share())

    this.renameModal = new RenameJobDialogViewModel(
      () => this.job,
      (newName) => this._renameDraft(newName),
      this._createAlertErrorHandler,
    )

    this.deleteDialogVm = new DeleteDraftJobDialogViewModel(this._draftJobStore)

    this.copyModal = new CopyJobDialogViewModel(
      () => this._copyDraft(this.job),
      this._navigate,
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this._lifecycle.manage(
      this.job$.pipe(
        switchMap(() =>
          merge(
            closeOnUnsubscribe(this.renameModal),
            closeOnUnsubscribe(this.copyModal),
          ),
        ),
      ),
    )

    this._lifecycle.manage(this.outline)
    for (const slice of this.stateSlices) {
      this._lifecycle.manage(slice)
    }

    this._lifecycle.manage(
      requestRename$.pipe(
        filter(() => this.jobWasCreated),
        tap(() => this.renameModal.show()),
      ),
    )

    // Only alerts; restoring the slices from the draft would discard the
    // user's input.
    this._lifecycle.manage(
      this.persister.persist$.pipe(
        filter((r) => !!r.errorMessage),
        tap((r) =>
          this._newAlert()
            .withState('error')
            .saying('Unable to save changes', r.errorMessage)
            .show(),
        ),
      ),
    )

    this._lifecycle.manage(
      merge(this.jobSubmitted$, this.jobDeleted$).pipe(
        // Lets every other subscriber see the emission before deactivating
        // tears down the shared streams.
        observeOn(asapScheduler),
        tap(() => this._finish()),
      ),
    )
  }

  /**
   * The step accessors.
   */
  @computed
  get stepAccessors(): StepAccessor[] {
    // Seeing as how this computed runs often,
    // using a plain loop is preferable.
    const stepAccessors: StepAccessor[] = []
    let breaker = false
    for (const slice of this.stateSlices) {
      for (const stepAccessor of slice.stepAccessors) {
        stepAccessors.push(stepAccessor)
        // If the step causes a short-circuit (Breaker), stop collecting.
        if (stepAccessor.step.isShortCircuited) {
          breaker = true
          break
        }
      }

      if (breaker) {
        break
      }
    }

    return stepAccessors
  }

  /**
   * The Job that is being drafted.
   */
  @computed
  get job() {
    return this.persister.job
  }

  /**
   * Whether the editor is completely disabled.
   */
  @computed
  get disabled(): boolean {
    return this._disabled() || this.submission.submit.pending
  }

  /**
   * Whether the job has been created or if we can still
   * make foundational changes to it.
   */
  @computed
  get jobWasCreated() {
    return this.persister.jobWasCreated
  }

  /**
   * Whether the draft was submitted or deleted.
   */
  @computed
  get isFinished() {
    return this._isFinished
  }

  /**
   * Whether to show the progress, which is only once we have the Legend and
   * the Draft has been created.
   */
  @computed
  get isProgressShown() {
    return (
      this.legendSelector.selectedLegendVersion !== PublishedLegend.none &&
      !(this.intakeQuestions.questionList?.isBroken ?? false) &&
      !this.intakeQuestions.isRunningFirstQuery &&
      this.job !== Job.none
    )
  }

  /**
   * The title of the active step.
   */
  @computed
  get title() {
    return this.outline.activeStep?.outlineDisplay.title || 'Welcome'
  }

  /**
   * Explains where going back leads to.
   */
  @computed
  get backTooltip() {
    return (
      this.outline.previousStep?.step.outlineDisplay.title ??
      'Unable to go back, this is the first step.'
    )
  }

  /**
   * When the draft was last saved, in full.
   */
  @computed
  get lastSavedTitle() {
    return `Last saved ${dateFormatters.dateTimeDefault.format(this.persister.lastSavedAt)}`
  }

  /**
   * How long ago the draft was last saved.
   *
   * Not computed, since it changes with the current time.
   */
  get lastSavedLabel() {
    const formatted = timeago(this.persister.lastSavedAt)
    return formatted === 'now' ? 'Saved just now' : `Saved ${formatted} ago`
  }

  /**
   * Whether the submission step is currently part of the outline.
   */
  @computed
  get isSubmissionAvailable() {
    return this.outline.stepAccessors.includes(this.submission)
  }

  /**
   * Whether questions, the Legend or the draft itself are loading or saving.
   */
  @computed
  get isBusy() {
    return (
      this.intakeQuestions.querying ||
      this.legendSelector.isFetchingLegendDetails ||
      this.persister.persisting
    )
  }

  /**
   * Whether to show the form's loader. Submission shows its own.
   */
  @computed
  get isFormLoading() {
    return !this.isSubmissionAvailable && this.isBusy
  }

  /**
   * View title.
   */
  @computed
  get viewTitle() {
    if (!this.prepare.resolved) {
      return 'Loading...'
    }

    return this.job === Job.none ? 'New Job' : this.job.displayName
  }

  /**
   * Whether the editor is for admins.
   */
  @computed
  get canEditDraftJobsAsAdmin(): boolean {
    const workspaceId = this.workspaceSelector.selectedWorkspace?.id
    if (!Id.some(workspaceId)) {
      return false
    }

    return this._canEditDraftJobsAsAdmin(workspaceId)
  }

  /**
   * When the parent controller is trying to load an instance of
   * the editor, if it is asked to load an editor for a draft that is
   * already set as the current editor, it may skip the preparation
   * step if the editor is revivable. If the editor is revivable,
   * you should call `revive`.
   *
   * The editor will be revivable when:
   *   - We are trying to edit an EXISTING draft (the ID is non-empty)
   *   - AND that ID represents the draft of this editor
   *   - AND the job is still a draft
   *   - AND it has been prepared
   *
   * @param jobId
   */
  isRevivable(jobId: Id) {
    if (!Id.some(jobId) || this.job.id !== jobId || this._isFinished) {
      return false
    }

    if (this.job.status !== 'UnderConstruction') {
      return false
    }

    return this.prepare.resolved
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Prepares the View Model with an existing draft, or fetching data
   * to prepare for a new draft. The editor must be active while preparing,
   * since the slices wait on each other's streams.
   *
   * Will be called when:
   *   1. Loading an existing draft
   *   2. Starting a new draft
   *
   * Will NOT be called after a server draft instance is created
   * in point 2 above, because at that point we already have that
   * draft's data loaded.
   *
   * @param jobId If specified, loads the Job
   * @param newDraft If creating a new draft, uses this prefilled input
   * @param signal
   */
  // Starts rejected (without an error) since preparation is needed until it
  // first succeeds.
  prepare = task.rejected(
    async (jobId: Id, newDraft: PrefilledDraftInput, signal: AbortSignal) => {
      await this._prepareImpl(jobId, newDraft, signal)
      signal.throwIfAborted()
    },
  )

  /**
   * Prompts to delete the draft.
   */
  @action.bound
  async promptDelete() {
    const deleted = await this.deleteDialogVm.prompt({
      job: this.job,
      signal: this._lifecycle.signal,
    })

    if (deleted) {
      this._jobDeleted.next()
    }
  }

  /**
   * Revives an active editor.
   * DO NOT call this unless `isRevivable` returns `true`!
   */
  revive() {
    this.stateSlices.forEach((slice) => slice.ready?.())
  }

  /**
   * Creates the Persister. Only called once.
   *
   * @private
   */
  private _createPersister(): DraftPersister {
    // This circumvents the circular dependency that the state slices
    // have on the Persister (the `job$` observable).
    // Basically, we defer creating the changes observable until the first
    // time the VM becomes active.
    // We also only want to persist changes while the VM is active.
    const changes$ = defer(() =>
      merge(...this.stateSlices.map((sc) => sc.changes$ ?? NEVER)),
    ).pipe(this._whileActive())

    return this._createDraftPersister(changes$)
  }

  /**
   * Stops editing the draft once it was submitted or deleted.
   *
   * @private
   */
  @action.bound
  private _finish() {
    this._isFinished = true
    this.deactivate()
  }

  /**
   * Queues a Rename change and waits for it to complete.
   *
   * @private
   */
  private async _renameDraft(newName: string) {
    const result = await this.persister.queueChange({
      type: 'Renamed',
      newName,
    })
    // Failed saves are already alerted.
    if (result.errorMessage) {
      return
    }

    this._newAlert()
      .withState('success')
      .saying('Success!', `Draft renamed to "${this.job.displayName}"`)
      .autoDismiss()
      .show()
  }

  /**
   * Copies the draft
   * @param job
   */
  private async _copyDraft(job: Job) {
    return this._jobStore.copyJob(job)
  }

  /**
   * Shortcut for the whileActive operator using the lifecycle's active stream.
   *
   * @private
   */
  private _whileActive<T>(): MonoTypeOperatorFunction<T> {
    return whileActive(this._lifecycle.active$)
  }

  /**
   * Prepare implementation.
   *
   * @param jobId
   * @param newDraft
   * @param signal
   * @private
   */
  private async _prepareImpl(
    jobId: Id,
    newDraft: PrefilledDraftInput,
    signal: AbortSignal,
  ) {
    // Wait for any pending flushes in case some were buffered.
    await this.persister.waitForFlush(signal)
    signal.throwIfAborted()

    // The Persister is responsible for the Job instance,
    // so tell it to fetch the Job (if there is one).
    try {
      await this.persister.prepare(jobId, signal)
    } catch (err) {
      // If we didn't find the job, tell the world that it was deleted.
      if (err instanceof NotFound) {
        this._jobDeleted.next()
      }
      throw err
    }

    signal.throwIfAborted()

    // The view passes in a `newDraft` context which will be used
    // for new drafts if no existing Job was loaded.
    const context: PrepareContext =
      this.job === Job.none
        ? { type: 'NewDraft', prefilledDraft: newDraft }
        : { type: 'EditDraft', job: this.job }

    // The Job (if any) has been loaded, time to prepare the
    // state slices.
    await this._prepareSlices(context, signal)

    // Starts persisting; any buffered changes will be persisted.
    this.persister.start()

    // If we are creating a new Draft and have enough info to persist it now,
    // wait for that to happen. In case we don't, the wait will resolve
    // immediately. We also wait for the first set of questions to load.
    // NOTE: Both of these complete on abort.
    // We wait for them in sequence because after the Persister has flushed...
    await this.persister.waitForFlush(signal)
    // ... the Intake might have a Job instance which means it started querying.
    await this.intakeQuestions.waitForNextQuery(signal)
  }

  /**
   * Prepares the state slices.
   *
   * @param context
   * @param signal
   * @private
   */
  private async _prepareSlices(context: PrepareContext, signal: AbortSignal) {
    await Promise.all(
      this.stateSlices.map(async (slice) => {
        await slice.prepare(context, signal)
        slice.ready?.()
      }),
    )
  }

  /**
   * Submit the request.
   *
   * @private
   */
  private async _submitRequest() {
    await this.persister.waitForFlush()

    // When the current actor has the ability to bypass billing,
    // the job's owner default payment method will be used.
    const paymentMethod = this.submission.billing.canBypassBilling
      ? null
      : this.submission.billing.selectedPaymentMethod

    const submissionId = await this._draftJobStore.submit({
      id: this.job.id,
      billingMethodId: paymentMethod?.id,
      bypassBilling: this.submission.billing.bypassBilling,
    })
    await this._draftJobStore.waitForSubmissionOutcome({
      submissionId,
      signal: this._lifecycle.signal,
    })
  }
}

/**
 * A slice of the draft job view-model's state.
 */
export interface DraftStateSlice extends ActivationLifecycle {
  /**
   * Step accessors for the slice of the state.
   */
  readonly stepAccessors: StepAccessor[]

  /**
   * Emits the changes to persist.
   */
  readonly changes$?: Observable<DraftChange>

  /**
   * Loads the data for an existing draft, or pre-fetches what is needed for
   * creating a new draft.
   *
   * The idea is that when loading an existing draft, we might not have to fetch
   * any heavy lists since the draft might already have a selection.
   *
   * Called at most once per view lifecycle, while the slice is active.
   *
   * Example: the Legend Selector will load **just the Legend** for an existing
   * draft, while it starts loading the Legend list for a new one.
   *
   * @param context
   * @param signal
   */
  prepare(context: PrepareContext, signal: AbortSignal): Promise<void>

  /**
   * If defined, called after `prepare` and when reviving. Slices whose
   * streams other slices wait on keep them silent until then, so that the
   * other slices don't prepare against a default value.
   */
  ready?(): void
}

/**
 * Context passed to `prepare`.
 */
export type PrepareContext = NewDraftContext | EditDraftContext

/**
 * Starting a new Draft. May contain some preselected stuff.
 */
export interface NewDraftContext {
  type: 'NewDraft'
  prefilledDraft: PrefilledDraftInput
}

/**
 * Editing an existing Draft.
 */
export interface EditDraftContext {
  type: 'EditDraft'
  job: Job
}

/**
 * Emits the modal and closes it on unsubscribe, so that it does not stay open
 * when the job changes or the editor deactivates.
 */
function closeOnUnsubscribe<T extends { modal: ModalViewModel }>(vm: T) {
  return new Observable<T>((subscriber) => {
    subscriber.next(vm)
    return () => vm.modal.close()
  })
}

/**
 * Factory for creating a {@link DraftJobViewModel}.
 */
export type DraftJobViewModelFactory = ReturnType<
  typeof createDraftJobViewModelFactory
>

/** @see DraftJobViewModelFactory */
export function createDraftJobViewModelFactory(
  createWorkspaceSelector: WorkspaceSelectorViewModelFactory,
  createOwnerSelector: OwnerSelectorViewModelFactory,
  createTeamSelector: TeamSelectorViewModelFactory,
  createSupportingClientSelector: SupportingClientSelectorViewModelFactory,
  createRequestProviderSelector: RequestProviderSelectorViewModelFactory,
  createLegendSelector: LegendSelectorViewModelFactory,
  createIntakeQuestions: IntakeQuestionsViewModelFactory,
  createPreviousProviderSelection: PreviousProviderSelectionViewModelFactory,
  createSubmission: SubmissionViewModelFactory,
  createDraftPersister: DraftPersisterFactory,
  jobStore: JobStore,
  draftJobStore: DraftJobStore,
  newAlert: AlertFactory,
  createAlertErrorHandler: AlertErrorHandlerFactory,
  navigate: NavigateFn,
  requestRename$: Observable<unknown>,
) {
  return (
    canEditDraftJobsAsAdmin: (workspaceId: Id) => boolean,
    disabled: () => boolean,
  ) =>
    new DraftJobViewModel(
      createWorkspaceSelector,
      createOwnerSelector,
      createTeamSelector,
      createSupportingClientSelector,
      createRequestProviderSelector,
      createLegendSelector,
      createIntakeQuestions,
      createPreviousProviderSelection,
      createSubmission,
      createDraftPersister,
      jobStore,
      draftJobStore,
      newAlert,
      createAlertErrorHandler,
      navigate,
      requestRename$,
      canEditDraftJobsAsAdmin,
      disabled,
    )
}
